class CartRemoveButton extends HTMLElement {
  async handleCartRemoval(event) {
    event.preventDefault();
    const target = event.target;
    try {
      const cartItems = this.closest('cart-items') || this.closest('cart-drawer-items');
      const index = this?.dataset?.index;

      // added troveUuid as a data attribute to the remove button to allow for easier removal of the item from the cart
      // this was primary for mixed styles, but changes were made to both mixed and traditional skus to support this
      const itemUuid = this?.dataset?.troveUuid;
      const variantId = this?.dataset?.variantId;
      const parentItem = target.closest('.main-cart-item');
      const itemQuantityEl = parentItem.querySelector('.product-option__quantity');
      let quantity = 0;
      if (itemQuantityEl) {
        quantity = parseInt(itemQuantityEl.querySelector('dd').textContent) - 1;
      }
      cartItems.updateQuantity(index, quantity, null, itemUuid, variantId);

      // Hide POPI cart message when item gets removed
      const currentUrl = window.location.pathname + window.location.search;
      const isMixedStylesPage = document.querySelector('#card-product-mixed-styles');
      if (!currentUrl.includes('/cart') && !isMixedStylesPage) {
        const shopifyCart = await SHOPIFY_UTILS.getShopifyCart();
        const { items } = shopifyCart;
        const cartItemIndex = this.attributes.getNamedItem('index');
        const itemIndex = cartItemIndex.value;
        const removedItemUrl = items[Number(itemIndex)].url;
        // when in the theme editor it adds additional params to the url causing a strict match to fail
        // switched to include to bypass that issue
        if (currentUrl.includes(removedItemUrl)) {
          const removedItemDiv = document.getElementById(`item-in-cart-${itemUuid}`);
          if (removedItemDiv) {
            const addToCartButton = document.getElementById('add-to-cart-button');
            addToCartButton.removeAttribute('disabled');
            const inputElement = document.querySelector(`input[value="${itemUuid}"]`);
            inputElement.removeAttribute('disabled');
            removedItemDiv.classList.add('hidden');
            addToCartButton.textContent = window.variantStrings.addToCart;
          }
        }
      }
      // at some point we should update popi's item selector to handle updating the badging and 
      // addToCart button state, but to avoid a regression while partners are migrating, this is
      // limited to just mixed styles
      if (isMixedStylesPage) {
        const itemRemovedEvent = new CustomEvent('itemremovedfromcart', { detail: itemUuid });
        document.dispatchEvent(itemRemovedEvent);
      }
    } catch (error) {
      console.error({ error });
    }
  }
  constructor() {
    super();
    this.addEventListener('click', this.handleCartRemoval.bind(this));
  }
}

customElements.define('cart-remove-button', CartRemoveButton);

class CartItems extends HTMLElement {
  constructor() {
    super();

    this.lineItemStatusElement = document.getElementById('shopping-cart-line-item-status') || document.getElementById('CartDrawer-LineItemStatus');

    this.currentItemCount = Array.from(this.querySelectorAll('[name="updates[]"]'))
      .reduce((total, quantityInput) => total + parseInt(quantityInput.value), 0);

    this.debouncedOnChange = debounce((event) => {
      this.onChange(event);
    }, 300);

    this.addEventListener('change', this.debouncedOnChange.bind(this));
  }

  onChange(event) {
    this.updateQuantity(
      event.target.dataset.index,
      event.target.value,
      document.activeElement.getAttribute('name'),
      event.target.dataset['troveUuid'],
      event.target.dataset['variantId']
    );
  }

  getSectionsToRender() {
    return [
      {
        id: 'cart-title',
        section: 'cart-title',
        selector: '.shopify-section',
      },
      {
        id: 'main-cart-items',
        section: document.getElementById('main-cart-items').dataset.id,
        selector: '.js-contents',
      },
      {
        id: 'cart-icon-bubble',
        section: 'cart-icon-bubble',
        selector: '.shopify-section'
      },
      {
        id: 'cart-live-region-text',
        section: 'cart-live-region-text',
        selector: '.shopify-section'
      },
      {
        id: 'main-cart-footer',
        section: document.getElementById('main-cart-footer').dataset.id,
        selector: '.js-contents',
      }
    ];
  }

  async removeItemFromTroveOrder(inventoryItemUuid, variantId = null) {
    const query = `
      mutation removeItemFromTroveOrder(
        $orderUuid: String
        $inventoryItemUuid: String
        $externalCartId: String
        $variantId: String
      ) {
        orderRemoveInventoryItem(
          orderUuid: $orderUuid
          inventoryItemUuid: $inventoryItemUuid
          externalCartId: $externalCartId
          variantId: $variantId
        ) {
          uuid
        }
      }
    `;

    const orderCart = await SHOPIFY_UTILS.getShopifyCart();
    const troveOrderUuid = orderCart.attributes.troveOrderUuid;
    const externalCartId = orderCart.token;
    const param = 'mutation-removeItemFromTroveOrder';

    try {
      const variables = {};

      // Use either orderUuid + inventoryItemUuid OR externalCartId + variantId
      if (inventoryItemUuid) {
        variables.orderUuid = troveOrderUuid;
        variables.inventoryItemUuid = inventoryItemUuid;
      } else if (variantId) {
        variables.externalCartId = externalCartId;
        variables.variantId = variantId;
      } else {
        throw new Error('Either inventoryItemUuid or variantId must be provided');
      }

      const response = await fetchWithAppName(`${global.url}${param}`, {
        body: JSON.stringify({ query, variables }),
      });

      const jsonData = await response.json();
      return jsonData.data;
    } catch (e) {
      console.error(e);
      return null;
    }
  }

  async updateQuantity(line, quantity, name, inventoryItemUuid = null, variantId = null) {
    this.enableLoading(line);
    if(inventoryItemUuid) {
      this.removeItemFromTroveOrder(inventoryItemUuid);
    } else if(variantId) {
      this.removeItemFromTroveOrder(null, variantId);
    }
    const sections = this.getSectionsToRender();
    const body = JSON.stringify({
      line,
      quantity,
      sections: sections.map((section) => section.section),
      sections_url: window.location.pathname
    });

    fetch(`${routes.cart_change_url}`, { ...fetchConfig(), ...{ body } })
      .then((response) => {
        return response.text();
      })
      .then(async (state) => {
        const parsedState = JSON.parse(state);
        this.classList.toggle('is-empty', parsedState.item_count === 0);
        const cartDrawerWrapper = document.querySelector('cart-drawer');
        const cartFooter = document.getElementById('main-cart-footer');

        if (cartFooter) cartFooter.classList.toggle('is-empty', parsedState.item_count === 0);
        if (cartDrawerWrapper) cartDrawerWrapper.classList.toggle('is-empty', parsedState.item_count === 0);

        const currentUrl = window.location.pathname + window.location.search;
        const isCartUrl = currentUrl.includes('/cart');

        // When we are on the cart page, we always have the list of cart items, but we could also have the cart drawer.
        if (isCartUrl) {
          // If we are calling this method from the cart item list, we will also need to update the cart drawer, and vice versa.
          const cartDrawer = this.closest('cart-drawer-items');
          const isCartDrawer = Boolean(cartDrawer);
          const cartSelector = isCartDrawer ? document.querySelector('cart-items') : document.querySelector('cart-drawer-items');
          if (cartSelector) {
            cartSelector.classList.toggle('is-empty', parsedState.item_count === 0);
            // We specify the list of sections we want to retrieve from Shopify through server-side rendering.
            const cartSelectorSections = cartSelector.getSectionsToRender();
            // These sections reside within the classes of cart item and cart drawer.
            const urlSections = cartSelectorSections.map(section => section.section).join(',');
            // The shopify api allows the sections to be comma separated.
            const response = await fetch(`${routes.cart_url}?sections=${urlSections}`);
            const cartState = await response.text();
            const parsedCartState = JSON.parse(cartState);
            // Merge both sections and states to perform a single render.
            sections.push(...cartSelectorSections);
            Object.assign(parsedState.sections, parsedCartState);
          }
        }

        this.renderSections(sections, parsedState);

        this.updateLiveRegions(line, parsedState.item_count);
        const lineItem = document.getElementById(`CartItem-${line}`) || document.getElementById(`CartDrawer-Item-${line}`);
        if (lineItem && lineItem.querySelector(`[name="${name}"]`)) {
          cartDrawerWrapper ? trapFocus(cartDrawerWrapper, lineItem.querySelector(`[name="${name}"]`)) : lineItem.querySelector(`[name="${name}"]`).focus();
        } else if (parsedState.item_count === 0 && cartDrawerWrapper) {
          trapFocus(cartDrawerWrapper.querySelector('.drawer__inner-empty'), cartDrawerWrapper.querySelector('a'))
        } else if (document.querySelector('.cart-item') && cartDrawerWrapper) {
          trapFocus(cartDrawerWrapper, document.querySelector('.cart-item__name'))
        }
        this.disableLoading();
      }).catch(() => {
        this.querySelectorAll('.loading-overlay').forEach((overlay) => overlay.classList.add('hidden'));
        const errors = document.getElementById('cart-errors') || document.getElementById('CartDrawer-CartErrors');
        errors.textContent = window.cartStrings.error;
        this.disableLoading();
      });
  }

  updateLiveRegions(line, itemCount) {
    if (this.currentItemCount === itemCount) {
      const lineItemError = document.getElementById(`Line-item-error-${line}`) || document.getElementById(`CartDrawer-LineItemError-${line}`);
      const quantityElement = document.getElementById(`Quantity-${line}`) || document.getElementById(`Drawer-quantity-${line}`);

      if (lineItemError) {
        lineItemError
          .querySelector('.cart-item__error-text')
          .innerHTML = window.cartStrings.quantityError.replace(
            '[quantity]',
            quantityElement.value
          );
      }
    }

    this.currentItemCount = itemCount;
    this.lineItemStatusElement.setAttribute('aria-hidden', true);

    const cartStatus = document.getElementById('cart-live-region-text') || document.getElementById('CartDrawer-LiveRegionText');
    cartStatus.setAttribute('aria-hidden', false);

    setTimeout(() => {
      cartStatus.setAttribute('aria-hidden', true);
    }, 1000);
  }

  getSectionInnerHTML(html, selector) {
    return new DOMParser()
      .parseFromString(html, 'text/html')
      .querySelector(selector).innerHTML;
  }

  enableLoading(line) {
    const mainCartItems = document.getElementById('main-cart-items') || document.getElementById('CartDrawer-CartItems');
    mainCartItems.classList.add('cart__items--disabled');

    const cartItemElements = this.querySelectorAll(`#CartItem-${line} .loading-overlay`);
    const cartDrawerItemElements = this.querySelectorAll(`#CartDrawer-Item-${line} .loading-overlay`);

    [...cartItemElements, ...cartDrawerItemElements].forEach((overlay) => overlay.classList.remove('hidden'));

    document.activeElement.blur();
    this.lineItemStatusElement.setAttribute('aria-hidden', false);
  }

  disableLoading() {
    const mainCartItems = document.getElementById('main-cart-items') || document.getElementById('CartDrawer-CartItems');
    mainCartItems.classList.remove('cart__items--disabled');
  }

  /**
   * Renders the specified sections with the provided parsed state.
   * @param {Array} sections - The array of sections to render.
   * @param {Object} parsedState - The parsed state containing section data.
   */
  renderSections(sections, parsedState) {
    sections.forEach((section => {
      const element = document.getElementById(section.id).querySelector(section.selector) || document.getElementById(section.id);
      if (!element) return;
      element.innerHTML = this.getSectionInnerHTML(parsedState.sections[section.section], section.selector);
    }));
  }
}

customElements.define('cart-items', CartItems);

if (!customElements.get('cart-note')) {
  customElements.define('cart-note', class CartNote extends HTMLElement {
    constructor() {
      super();

      this.addEventListener('change', debounce((event) => {
        const body = JSON.stringify({ note: event.target.value });
        fetch(`${routes.cart_update_url}`, { ...fetchConfig(), ...{ body } });
      }, 300))
    }
  });
};
